import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ResaleEmptyState from '../components/ResaleEmptyState';
import { ResaleListingAccordeon } from '../components/ResaleListingAccordeon';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import { useResaleListingContext } from '../hooks/contexts/resale-listing.context';
import type { ResaleListingGroup } from '../hooks/contexts/resale-listing.context';
import { useResaleEventContext } from '../hooks/contexts/resaleEvent.context';
import { logger } from '../utils/logger';

type ResaleListingParams = {
  identifier: string;
};

/**
 * ResaleListingPage — displays available resale tickets for an event.
 * Prices shown are finalPrice (FastPass spread already applied by backend).
 * saleValue is intentionally not available in this flow.
 * UI is a functional placeholder — detailed design is out of scope for this PR.
 */
export const ResaleListingPage = () => {
  const { identifier } = useParams<ResaleListingParams>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const posthog = usePostHog();
  const { translate } = useI18nContext();
  const { groups, loading, hasError, fetchListingGrouped } = useResaleListingContext();
  const { fetchEventById } = useResaleEventContext();

  const source = searchParams.get('source') ?? undefined;
  const waitlistId = searchParams.get('waitlist_id') ?? undefined;

  const buildNavState = (finalPrice: number) => ({ source, waitlistId, overridePrice : finalPrice });

  useEffect(() => {
    if (identifier) {
      fetchListingGrouped(identifier);
    } else {
      logger.warn('[ResaleListingPage] No identifier found in URL params');
    }
    // fetchListingGrouped is a stable useCallback — safe to omit from deps
  }, [identifier]);

  useEffect(() => {
    const eventId = groups[0]?.eventId;
    if (eventId) fetchEventById(eventId);
    // fetchEventById omitted from deps — unstable context function, same pattern as ResalePage
  }, [groups]);

  const totalAvailable = groups.reduce((sum, g) => sum + g.count, 0);

  useEffect(() => {
    if (!loading && groups.length > 0) {
      posthog?.capture('resale_listing_page_view', {
        event_identifier : identifier,
        item_count : totalAvailable,
        source,
        has_waitlist_id : !!waitlistId,
      });
    }
  }, [loading, groups.length, identifier, posthog, source, waitlistId, totalAvailable]);

  if (loading) {
    return null;
  }

  if (hasError || groups.length === 0) {
    const titleKey = hasError ? 'ERROR_TITLE' : 'EMPTY_TITLE';
    const subtitleKey = hasError ? 'ERROR_SUBTITLE' : 'EMPTY_SUBTITLE';
    return (
      <ResaleEmptyState
        titleOverride={translate(['RESALE_LISTING', titleKey])}
        subtitleOverride={translate(['RESALE_LISTING', subtitleKey])}
      />
    );
  }

  const handleSelectGroup = (group: ResaleListingGroup) => {
    const path = group.offerId
      ? `/resale/${group.uuid}?offerId=${group.offerId}`
      : `/resale/${group.uuid}`;
    navigate(path, { state : buildNavState(group.finalPrice) });
  };

  return (
    <ResaleListingAccordeon
      groups={groups}
      onSelectGroup={handleSelectGroup}
    />
  );
};
